import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { lazy, useState } from "react";
import { Home, Users, Volume2, VolumeX, LogIn, LogOut, FolderHeart, KeyRound, LayoutDashboard, Crown, ChevronDown, Menu, Keyboard, Gamepad2, Blocks, Grid3x3, Rocket, Code2, Paintbrush, Globe2, Building2, Lightbulb, Network, PenTool } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { Sheet, SheetTrigger, SheetContent, SheetTitle } from "./ui/sheet";
import { StaffAvatar } from "./tenant/StaffAvatar";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { MASCOT_IMAGE, OWL_ORANGE } from "../assets";
import { roleHome } from "../lib/api";
import { OrgLogo } from "./OrgLogo";
import { PlanBanner } from "./PlanBanner";
import { TermsGate } from "./TermsGate";
import { SiteFooter } from "./SiteFooter";
import { Toaster } from "sonner";
import EduHome from "../pages/EduHome";
import { EduChrome } from "../pages/EduHome";
import { SchoolsRibbon } from "./SchoolsRibbon";
import { isMainHost, isKidsHost, mainHref, KIDS_PATH } from "../lib/hosts";
import ChangePasswordModal from "./ChangePasswordModal";
import { ProfileSwitcher } from "./ProfileSwitcher";
import { NotificationBell } from "./pwa/NotificationBell";
import { InstallButton } from "./pwa/InstallButton";
import { AvatarImg } from "./tenant/Avatar";
import { isKidPath } from "../kid/paths";
import { useFollowPull } from "../kid/useFollowPull";
import { AgeBar } from "./AgeToggle";
import { DemoBanner } from "./DemoBanner";
// Loaded only when needed, so the start page downloads less
const LmsPricing = lazy(() => import("../pages/LmsPricing"));
const KidShell = lazy(() => import("../kid/KidShell").then((m) => ({ default: m.KidShell })));

const LEARN = [
  { to: "/lessons", match: "/lesson", testid: "nav-lessons", label: "learnTyping", desc: "learnTypingDesc", Icon: Keyboard, color: "#0D9488" },
  { to: "/games", match: "/game", testid: "nav-games", label: "games", desc: "gamesDesc", Icon: Gamepad2, color: "#7C3AED" },
  { to: "/adventures", match: "/adventures", testid: "nav-adventures", label: "adventures", desc: "adventuresDesc", Icon: Blocks, color: "#0038EB" },
  { to: "/pixel-trial", match: "/pixel", testid: "nav-digital-explorers", label: "digitalExplorers", desc: "digitalExplorersDesc", Icon: Grid3x3, Rocket, color: "#FF8A00" },
  { to: "/code-trial", match: "/code-trial", testid: "nav-code-trial", label: "codeTrial", desc: "codeTrialDesc", Icon: Rocket, color: "#070B2A" },
  { to: "/python-trial", match: "/python-trial", testid: "nav-python-trial", label: "pythonTrial", desc: "pythonTrialDesc", Icon: Code2, color: "#3776AB" },
];
const THINK = [
  { to: "/design-thinking", match: "/design-thinking", testid: "nav-design-thinking", label: "designThinking", desc: "designThinkingDesc", Icon: Lightbulb, color: "#0038EB" },
  { to: "/mind-map", match: "/mind-map", testid: "nav-mind-map", label: "mindMap", desc: "mindMapDesc", Icon: Network, color: "#DB2777" },
  { to: "/whiteboard", match: "/whiteboard", testid: "nav-whiteboard", label: "whiteboard", desc: "whiteboardDesc", Icon: PenTool, color: "#059669" },
];
const THINK_MATCH = ["/design-thinking", "/mind-map", "/whiteboard", "/canvas"];

function NavGroup({ items, label, active, testid, menuTestid, t, path }) {
  return <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid={testid} className={cn("inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold transition-all", active ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100")}>
        {label} <ChevronDown size={15} className="opacity-80" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" sideOffset={8} className="w-72 p-2 rounded-2xl shadow-xl border-slate-200" data-testid={menuTestid}>
      {items.map((it) => (
        <DropdownMenuItem key={it.to} asChild className="p-0 rounded-xl focus:bg-slate-100">
          <Link to={it.to} data-testid={it.testid} className={cn("flex items-center gap-3 px-3 py-2.5 min-h-[48px] rounded-xl", path.startsWith(it.match) && "bg-teal-50")}>
            <span className="grid place-items-center w-9 h-9 rounded-xl text-white shrink-0" style={{ backgroundColor: it.color }}><it.Icon size={18} /></span>
            <span className="min-w-0"><span className="block text-sm font-bold text-slate-800">{t(it.label)}</span><span className="block text-xs text-slate-500 truncate">{t(it.desc)}</span></span>
          </Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>;
}

// About, How to use, Pricing (and "For schools" on the kids site) in one dropdown, so the bar stays short.
function MoreMenu({ t, lang, path }) {
  const active = TOP.some((it) => path.startsWith(it.to));
  const row = "flex items-center px-3 py-2.5 min-h-[44px] rounded-xl text-sm font-bold";
  return <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid="nav-more" className={cn("inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold transition-all", active ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100")}>
        {lang === "ar" ? "المزيد" : "More"} <ChevronDown size={15} className="opacity-80" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" sideOffset={8} className="w-56 p-2 rounded-2xl shadow-xl border-slate-200" data-testid="more-menu">
      {TOP.map((it) => <DropdownMenuItem key={it.to} asChild className="p-0 rounded-xl focus:bg-slate-100">
        <Link to={it.to} data-testid={it.testid} className={cn(row, "text-slate-700", path.startsWith(it.to) && "bg-teal-50")}>{t(it.label)}</Link>
      </DropdownMenuItem>)}
      {isKidsHost() && <DropdownMenuItem asChild className="p-0 rounded-xl focus:bg-blue-50">
        <a href={mainHref("/")} data-testid="nav-lms" className={cn(row, "gap-2 text-[#0038EB]")}><Building2 size={15} /> {t("navLms")}</a>
      </DropdownMenuItem>}
    </DropdownMenuContent>
  </DropdownMenu>;
}

function MobileGroup({ items, label, t, path, close }) {
  return <>
    <div className="px-3 text-xs font-bold uppercase tracking-wider text-slate-400 mb-1 mt-2">{label}</div>
    {items.map((it) => (
      <Link key={it.to} to={it.to} data-testid={`m-${it.testid}`} onClick={close} className={cn("flex items-center gap-3 px-3 py-3 rounded-xl min-h-[52px]", path.startsWith(it.match) ? "bg-teal-50" : "hover:bg-slate-50")}>
        <span className="grid place-items-center w-10 h-10 rounded-xl text-white shrink-0" style={{ backgroundColor: it.color }}><it.Icon size={20} /></span>
        <span><span className="block text-base font-bold text-slate-800">{t(it.label)}</span><span className="block text-xs text-slate-500">{t(it.desc)}</span></span>
      </Link>
    ))}
  </>;
}

const TOP = [
  { to: "/about", testid: "nav-about", label: "about" },
  { to: "/how-to-use", testid: "nav-howto", label: "howToUse" },
  { to: "/pricing", testid: "nav-pricing", label: "pricing" },
];

const MAIN_PUBLIC = ["/about", "/login", "/register", "/forgot-password", "/reset-password", "/terms", "/trial-terms", "/security", "/how-to-use"];

export function Layout() {
  const { t, lang, setLang, sound, setSound } = useApp();
  const { isAuthed, user, logout, role, isStaff } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [showPwd, setShowPwd] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const inLearn = LEARN.some((it) => loc.pathname.startsWith(it.match));
  const inThink = THINK_MATCH.some((m) => loc.pathname.startsWith(m));
  const inDash = loc.pathname.startsWith("/trainer") || loc.pathname.startsWith("/center") || loc.pathname.startsWith("/admin");
  const isStudent = role === "student";
  const dashPath = isStaff ? roleHome(role) : null;
  useFollowPull(isAuthed && isStudent, lang);  // teacher's "Follow me" brings the kid's tablet into the book

  // The main domain's "/" is the institutions (LMS) home; /kids (and kids mode) keeps this family layout untouched.
  if (loc.pathname === "/" && isMainHost()) return <EduHome />;
  if (loc.pathname === "/pricing" && isMainHost()) return <LmsPricing />;
  if (MAIN_PUBLIC.includes(loc.pathname) && isMainHost()) return (  // main site: one menu (LandingNav) + landing footer on every public page
    <EduChrome testid="main-public-page">
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 sm:py-10"><Outlet /><TermsGate /><Toaster position="top-center" offset={80} richColors /></main>
    </EduChrome>
  );
  const kidsHome = loc.pathname === "/" || loc.pathname === KIDS_PATH;
  if (isAuthed && isStudent && isKidPath(loc.pathname)) return <KidShell />;  // kid area: soft-3D shell with Back/Home on every page

  return (
    <div className="min-h-screen dotted-bg flex flex-col">
      <DemoBanner />
      {isKidsHost() && kidsHome && <SchoolsRibbon />}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/95 border-b border-slate-200 text-slate-700 thm-menu">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <Link to={isKidsHost() ? KIDS_PATH : "/"} data-testid="nav-home" className="flex items-center gap-2 font-display font-bold text-xl me-2">
            <span className="grid place-items-center w-10 h-10">
              <img src={OWL_ORANGE} alt="Owl logo" className="w-10 h-10 object-contain drop-shadow-md" />
            </span>
            {lang === "en"
              ? <img src="/img/buma-logo.webp" alt="BUMA — Navigate · Think · Build · Succeed" className="h-7 sm:h-8 w-auto" />
              : <img src="/img/buma-logo-ar.webp" alt="بومة — نستكشف · نفكر · نبني · ننجح" className="h-8 sm:h-9 w-auto" />}
            <span className="hidden xl:inline text-xs font-normal text-slate-400">{t("by")}</span>
            <img src="/img/ahdaf-logo.webp" alt="ahdaf" className="hidden xl:inline h-4 w-auto opacity-95" />
          </Link>
          {user?.org_id && <span className="hidden sm:flex items-center gap-2 me-2 ps-3 border-s border-slate-200"><OrgLogo orgId={user.org_id} className="h-8" testid="header-org-logo" /></span>}

          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            <NavGroup items={LEARN} label={t("learnMenu")} active={inLearn} testid="nav-learn" menuTestid="learn-menu" t={t} path={loc.pathname} />
            <NavGroup items={THINK} label={t("thinkMenu")} active={inThink} testid="nav-think" menuTestid="think-menu" t={t} path={loc.pathname} />
            <MoreMenu t={t} lang={lang} path={loc.pathname} />
            {isAuthed && role === "student" && (
              <Link to="/home" data-testid="nav-student-home" className={cn("px-3 py-1.5 rounded-full text-sm font-bold transition-all inline-flex items-center gap-1", loc.pathname === "/home" ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100")}><Home size={15} /> {t("homeNav")}</Link>
            )}
            {isAuthed && role === "student" && (
              <Link to="/activities" data-testid="nav-activities" className={cn("px-3 py-1.5 rounded-full text-sm font-bold transition-all inline-flex items-center gap-1", loc.pathname.startsWith("/activit") ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100")}>
                <Paintbrush size={15} /> {t("myActivities")}
              </Link>
            )}
            {isAuthed && (<>
              <Link to="/community" data-testid="nav-community" className={cn("px-3 py-1.5 rounded-full text-sm font-bold transition-all inline-flex items-center gap-1", loc.pathname.startsWith("/community") ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-100")}><Globe2 size={15} /> {t("community")}</Link>
              <Link to="/projects" data-testid="nav-projects" className={cn("px-3 py-1.5 rounded-full text-sm font-bold transition-all inline-flex items-center gap-1", loc.pathname.startsWith("/projects") ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100")}>
                <FolderHeart size={15} /> {t("myProjects")}
              </Link>
            </>)}
            {isStaff && (
              <Link to={dashPath} data-testid="nav-dashboard" className={cn("px-3 py-1.5 rounded-full text-sm font-bold transition-all inline-flex items-center gap-1", inDash ? "bg-[#0038EB] text-white" : "text-slate-600 hover:bg-slate-100")}>
                <LayoutDashboard size={15} /> {t("dashboard")}
              </Link>
            )}
          </nav>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-menu-btn" className="md:hidden grid place-items-center w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200" aria-label={t("menu")}><Menu size={20} /></button>
            </SheetTrigger>
            <SheetContent side={lang === "ar" ? "right" : "left"} className="w-[88vw] max-w-sm p-0 overflow-y-auto thm-menu" data-testid="mobile-menu">
              <div className="p-5 pb-3"><SheetTitle className="font-display font-bold text-xl text-slate-800">{t("menu")}</SheetTitle></div>
              <div className="px-3">
                <MobileGroup items={LEARN} label={t("learnMenu")} t={t} path={loc.pathname} close={() => setMenuOpen(false)} />
                <MobileGroup items={THINK} label={t("thinkMenu")} t={t} path={loc.pathname} close={() => setMenuOpen(false)} />
                <div className="my-3 border-t border-slate-100" />
                {isKidsHost() && <a href={mainHref("/")} data-testid="m-nav-lms" className="flex items-center gap-2 px-3 py-3 rounded-xl text-base font-bold text-[#0038EB] min-h-[48px]"><Building2 size={18} /> {t("navLms")}</a>}
                {TOP.map((it) => (
                  <Link key={it.to} to={it.to} data-testid={`m-${it.testid}`} onClick={() => setMenuOpen(false)} className={cn("block px-3 py-3 rounded-xl text-base font-bold min-h-[48px]", loc.pathname.startsWith(it.to) ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-slate-50")}>{t(it.label)}</Link>
                ))}
                {isAuthed && <Link to="/community" data-testid="m-nav-community" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-3 rounded-xl text-base font-bold text-slate-700 hover:bg-slate-50 min-h-[48px]"><Globe2 size={17} /> {t("community")}</Link>}
                {isAuthed && <Link to="/projects" data-testid="m-nav-projects" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-3 rounded-xl text-base font-bold text-slate-700 hover:bg-slate-50 min-h-[48px]"><FolderHeart size={17} /> {t("myProjects")}</Link>}
                {isStaff && <Link to={dashPath} data-testid="m-nav-dashboard" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-3 rounded-xl text-base font-bold text-[#0038EB] hover:bg-blue-50 min-h-[48px]"><LayoutDashboard size={17} /> {t("dashboard")}</Link>}
                {!isAuthed && <button data-testid="m-login-btn" onClick={() => { setMenuOpen(false); navigate("/login"); }} className="mt-3 mb-5 w-full inline-flex items-center justify-center gap-1.5 bg-teal-600 text-white font-bold text-base px-3 py-3 rounded-2xl"><LogIn size={17} /> {t("login")}</button>}
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2 ms-auto flex-wrap">
            {/* one button: shows the other language */}
            <button type="button" data-testid={lang === "ar" ? "lang-toggle-en" : "lang-toggle-ar"} onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="grid place-items-center min-w-9 h-9 px-2 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-bold text-slate-700 transition-colors" aria-label={lang === "ar" ? "English" : "العربية"}>
              {lang === "ar" ? "EN" : "ع"}
            </button>

            <button
              data-testid="sound-toggle"
              onClick={() => setSound((s) => !s)}
              className="grid place-items-center w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
              aria-label={t("sound")}
            >
              {sound ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>

            {isAuthed ? (
              <>
              {role === "parent" && user?.family_portal && (
              <Link to="/family" data-testid="nav-family-portal" className="px-3 py-1.5 rounded-full text-sm font-bold bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors">{lang === "ar" ? "بوابة الأسرة" : "Family portal"}</Link>)}
              {role === "parent" && !user?.family_portal && (
              <Link to="/account/plan" data-testid="nav-plan" title={t("planTitle")} aria-label={t("planTitle")}
                className={cn("grid place-items-center w-9 h-9 rounded-full transition-colors", loc.pathname === "/account/plan" ? "bg-[#0038EB] text-white" : user?.subscription?.locked ? "bg-orange-100 text-orange-700 hover:bg-orange-200" : "bg-slate-100 hover:bg-slate-200")}>
                <Crown size={17} />
              </Link>
              )}
              <InstallButton lang={lang} compact className="grid place-items-center w-9 h-9 rounded-full bg-[#0038EB] text-white hover:bg-[#002FC4]" />
              <NotificationBell lang={lang} />
              {!isStudent && <ProfileSwitcher />}
              {!isStudent && (
              <button data-testid="change-password-btn" onClick={() => setShowPwd(true)}
                className="grid place-items-center w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" title={t("changePassword")} aria-label={t("changePassword")}>
                <KeyRound size={17} />
              </button>
              )}
              {isStudent && <button data-testid="switch-kid-btn" onClick={() => { logout(); navigate("/join"); }} className="inline-flex items-center gap-1.5 bg-[#0038EB] hover:bg-[#002FC4] text-white font-bold text-sm px-3 py-1.5 rounded-full transition-colors"><Users size={15} /> <span>{lang === "ar" ? "تبديل الطفل" : "Switch kid"}</span></button>}
              <button data-testid="logout-btn" onClick={() => { logout(); navigate(isStudent ? "/join" : "/"); }}
                className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm px-3 py-1.5 rounded-full transition-colors">
                {isStudent ? <AvatarImg avatar={user?.avatar} size={22} className="border-0 bg-transparent" /> : isStaff ? <StaffAvatar name={user?.name} avatarUrl={user?.avatar_url} size={22} testid="header-staff-avatar" /> : <LogOut size={15} />}
                <span className="hidden sm:inline" data-testid="nav-user-name">{isStudent ? user?.display_name : `${t("loggedInAs")} ${user?.child_full_name || user?.name || ""}`}</span>
                {isStudent && <LogOut size={14} className="text-slate-400" />}
              </button>
              </>
            ) : (
              <button data-testid="login-btn" onClick={() => navigate("/login")}
                className="inline-flex items-center gap-1.5 bg-teal-600 text-white font-bold text-sm px-3 py-1.5 rounded-full hover:bg-teal-700 transition-colors">
                <LogIn size={15} /> {t("login")}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 sm:py-10">
        <PlanBanner />
        <AgeBar />
        <Outlet />
        <TermsGate />
        <Toaster position="top-center" offset={80} richColors />
      </main>
      {["/", "/how-to-use", "/pricing", "/terms", "/security", "/trial-terms"].includes(loc.pathname) && <SiteFooter />}
      <ChangePasswordModal open={showPwd} onClose={() => setShowPwd(false)} t={t} />
    </div>
  );
}
